"use client"

import {
  FilterSection,
  type FilterState,
} from "@/components/filters/filter-section"
import {
  filtersToUrlParams,
  sortingOptions,
  urlParamsToFilters,
} from "@/components/filters/helpers"
import dynamic from "next/dynamic"
import { useRouter, useSearchParams } from "next/navigation"
import {
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react"
import { ProductsGrid } from "./products-grid"
import { ProductsHeader } from "./products-header"
const MobileFilterDrawer = dynamic(
  async () => {
    const mod = await import("@/components/filters/mobile-filter-drawer")
    return mod.MobileFilterDrawer
  },
  { ssr: false }
)

import { BackButton } from "@/components/back-button"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { ArrowDown01Icon, FilterVerticalIcon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { Check } from "lucide-react"
import { Breadcrumb } from "../../_components/Breadcrum"
import type { FiltersResponse, ProductsResponse } from "../actions"

interface AllProductsClientProps {
  initialData: ProductsResponse
  filtersData: FiltersResponse
  initialFilters: FilterState
  initialSortBy: string
  searchQuery: string
}

export default function AllProductsClient({
  initialData,
  filtersData,
  initialFilters,
  initialSortBy,
  searchQuery: initialSearchQuery,
}: AllProductsClientProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()
  const [activeFilters, setActiveFilters] =
    useState<FilterState>(initialFilters)
  const [sortBy, setSortBy] = useState(initialSortBy)
  const [sortDrawer, setSortDrawer] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const searchQuery = useMemo(
    () => searchParams?.get("q") ?? initialSearchQuery,
    [searchParams, initialSearchQuery]
  )
  const debouncedSearchQuery = useDeferredValue(searchQuery)

  useEffect(() => {
    const params = filtersToUrlParams(activeFilters, sortBy)
    if (searchQuery) params.set("q", searchQuery)
    const newUrl = `?${params.toString()}`

    startTransition(() => {
      router.replace(newUrl)
    })
  }, [activeFilters, sortBy, searchQuery])

  useEffect(() => {
    const { filters: updatedFilters, sortBy: updatedSortBy } =
      urlParamsToFilters(searchParams)

    let hasChanges = false
    if (JSON.stringify(updatedFilters) !== JSON.stringify(activeFilters)) {
      setActiveFilters(updatedFilters)
      hasChanges = true
    }
    if (updatedSortBy !== sortBy) {
      setSortBy(updatedSortBy)
      hasChanges = true
    }

    if (hasChanges) {
      setIsUpdating(true)
    }
  }, [searchParams])

  useEffect(() => {
    if (debouncedSearchQuery !== initialSearchQuery) {
      setIsUpdating(true)
    }
  }, [debouncedSearchQuery, initialSearchQuery])

  useEffect(() => {
    if (!isPending) {
      setIsUpdating(false)
    }
  }, [isPending])

  const handleFilterChange = (filters: FilterState) => setActiveFilters(filters)
  const handleSortChange = (value: string) => setSortBy(value)

  const products = initialData?.products || []
  const totalCount = initialData?.totalCount || 0

  const filteredProducts = sortBy === "low" || sortBy === "high" || sortBy === "recent" ? products : [...products].sort((a: any, b: any) => {
    const Aprice = a.payable / 100
    const Bprice = b.payable / 100
    if (a.qty === 0 && b.qty !== 0) return 1
    if (b.qty === 0 && a.qty !== 0) return -1
    if (Bprice !== Aprice) return Aprice - Bprice
    return b.qty - a.qty
  })

  const isLoading = isPending || isUpdating

  const handleSortOptionClick = (value: string) => () => handleSortChange(value)

  return (
    <section className="flex flex-col items-center justify-center w-full my-8">
      <div className="flex w-full items-center mb-6">
        <div className="items-center gap-3 sm:w-[30%] sm:flex hidden">
          <BackButton className="text-xs" />
          <Breadcrumb
            className="font-gilroyRegular text-sm w-full"
            items={[
              { label: "Home", href: "/" },
              { label: "All Refurbished Products", href: "/all-products" },
            ]}
          />
        </div>
        <div className="w-full sm:w-[60%] flex justify-center items-center">
          <h1 className="text-center font-orange text-xl lg:text-3xl xl:text-4xl">
            All Products
          </h1>
        </div>
      </div>

      <div className="flex bg-[#F7F7F7] w-full rounded-[7px] items-center justify-between px-4 sm:hidden">
        <MobileFilterDrawer
          data={filtersData}
          onFilterChange={handleFilterChange}
          initialFilters={initialFilters}
        >
          <button className="flex gap-2 items-center rounded-[6px] px-4 py-2">
            <HugeiconsIcon icon={FilterVerticalIcon} size={16} />
            <span className="text-black font-gilroySemiBold text-sm">
              Filters
            </span>
            <HugeiconsIcon icon={ArrowDown01Icon} size={16} />
          </button>
        </MobileFilterDrawer>

        <div className="text-[#E1E1E1]">|</div>

        <button
          className="flex gap-2 items-center rounded-[6px] px-4 py-2"
          onClick={() => setSortDrawer(!sortDrawer)}
        >
          <img
            src="/sort.svg"
            alt="sortIcon"
            loading="lazy"
            className="size-4"
          />
          <span className="text-black font-gilroySemiBold text-sm">
            Sort By
          </span>

          <HugeiconsIcon icon={ArrowDown01Icon} size={16} />
        </button>
      </div>

      {sortDrawer && (
        <Drawer open={sortDrawer} onClose={() => setSortDrawer(false)}>
          <DrawerHeader className="sr-only">
            <DrawerTitle>Sorting</DrawerTitle>
          </DrawerHeader>
          <DrawerContent>
            <div className="p-2">
              {sortingOptions.map((option) => (
                <p
                  key={option.value}
                  className="px-4 py-2 cursor-pointer flex justify-between items-center hover:bg-[#F7F7F7] hover:rounded-[6px] text-sm font-gilroySemiBold"
                  onClick={handleSortOptionClick(option.value)}
                >
                  <span>{option.label}</span>
                  {sortBy === option.value && <Check className="size-4" />}
                </p>
              ))}
            </div>
          </DrawerContent>
        </Drawer>
      )}

      <div className="w-full flex gap-8">
        <FilterSection
          data={filtersData}
          initialFilters={activeFilters}
          onFilterChange={handleFilterChange}
          className="sm:w-[30%] sm:block hidden"
        />

        <div className="w-full flex flex-col gap-3 relative max-sm:w-full">
          <ProductsHeader
            totalCount={totalCount}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            isLoading={isLoading}
            searchQuery={debouncedSearchQuery}
          />

          <ProductsGrid
            products={filteredProducts}
            isLoading={isLoading}
            error={null}
          />
        </div>
      </div>
    </section>
  )
}
